import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Theme-aware radio control (matches Checkbox accent / border tokens).
 *
 * @param {Object} props
 * @param {boolean} [props.checked]
 * @param {function(): void} [props.onSelect]
 * @param {boolean} [props.disabled]
 * @param {string} [props.className]
 * @param {string} [props.id]
 * @param {string} [props.name]
 * @param {string} [props.value]
 * @returns {JSX.Element}
 */
const Radio = React.forwardRef(
  ({ className, checked = false, onSelect, disabled, id, name, value, ...props }, ref) => {
    return (
      <button
        type="button"
        role="radio"
        id={id}
        name={name}
        value={value}
        aria-checked={checked}
        disabled={disabled}
        ref={ref}
        {...props}
        onClick={(event) => {
          props.onClick?.(event);
          if (event.defaultPrevented || disabled) return;
          onSelect?.();
        }}
        className={cn(
          "inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors duration-200 ease-out",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui-background",
          "disabled:cursor-not-allowed disabled:opacity-50",
          checked
            ? "border-accent bg-ui-card text-accent"
            : "border-ui-border bg-ui-card text-transparent",
          className
        )}
      >
        <span
          aria-hidden
          className={cn(
            "h-[42%] w-[42%] rounded-full bg-current",
            checked ? "opacity-100" : "opacity-0"
          )}
        />
      </button>
    );
  }
);
Radio.displayName = "Radio";

export { Radio };
